import { useAuth0 } from "@appretio/auth";
import { Chatbot, ChatProvider } from "@appretio/chat";
import { FeedbackModal, FeedbackProvider } from "@appretio/feedback";
import { SheetContainer, SheetContainerProvider } from "@appretio/ui-kit";
import { Outlet, RouteObject, useLocation, useMatches } from "react-router-dom";
import { FeedbackRestoreNotice } from "../feedbackRestoreNotice/FeedbackRestoreNotice";
import { RouteFeedbackContext } from "../routeFeedbackContext/RouteFeedbackContext";
import { AppHeader } from "./AppHeader";
import { layoutContainer, main } from "./AppLayout.css";

const AUTH_DISABLED = import.meta.env.VITE_AUTH_DISABLED === "true";

interface AppLayoutProps {
  /**
   * The navigation routes for the application.
   */
  navRoutes: RouteObject[];
  /**
   * The background images for the application.
   */
  backgrounds?: Record<string, string>;
  /**
   * The default background for the application.
   */
  defaultBackground?: string;
}

type RouteHandle = {
  /**
   * The background image for the route.
   */
  background?: string;
  /**
   * Additional route metadata.
   */
  [key: string]: unknown;
};

export function AppLayout({
  navRoutes,
  defaultBackground = "inherit"
}: AppLayoutProps) {
  const { user, logout } = useAuth0();

  function handleLogout() {
    if (AUTH_DISABLED) {
      window.location.assign(window.location.origin);
      return;
    }
    logout({
      logoutParams: {
        returnTo: window.location.origin
      }
    });
  }

  const displayName = AUTH_DISABLED ? "Buster Bypass" : (user?.name ?? "");

  const matches = useMatches();
  const parentRoute = matches[matches.length - 2];
  const currentRoute = matches[matches.length - 1];

  const parentMeta = parentRoute?.handle as RouteHandle | undefined;
  const currentMeta = currentRoute?.handle as RouteHandle | undefined;

  const background =
    currentMeta?.background ?? parentMeta?.background ?? defaultBackground;

  const { pathname } = useLocation();

  return (
    <ChatProvider>
      <FeedbackProvider>
        <div
          data-testid="app-layout"
          style={{ background }}
          className={layoutContainer}
        >
          <AppHeader
            navRoutes={navRoutes}
            pathname={pathname}
            userName={displayName}
            onSignOut={handleLogout}
          />

          {/* Between header and main: <main> lays pages out as a flex row. */}
          <FeedbackRestoreNotice />

          <main className={main}>
            <SheetContainerProvider>
              <Outlet />
              <SheetContainer />
            </SheetContainerProvider>
          </main>

          <Chatbot />
          <RouteFeedbackContext />
          <FeedbackModal />
        </div>
      </FeedbackProvider>
    </ChatProvider>
  );
}
